import React, { lazy, Suspense, useState, useEffect, useLayoutEffect } from 'react';
import { Router, Routes, Route } from 'react-router-dom';
import { browserHistory } from './history';
import { PageLoader } from './components';

import DashboardRoute from './routes/DashboardRoute';
import PrepareRoute from './routes/PrepareRoute';
import PrepareRouteTemplate from './routes/PrepareRouteTemplate';
import PrepareRouteTemplateUse from './routes/PrepareRouteTemplateUse';
import PublicRoute from './routes/PublicRoute';
import SignRoute from './routes/SignRoute';

import Login from './modules/Login';
import ForgotPassword from './modules/Login/ForgotPassword';
import ForgotPasswordSuccess from './modules/Login/ForgotPasswordSuccess';
import PasswordReset from './modules/Login/PasswordReset';
import PasswordResetSuccess from './modules/Login/PasswordResetSuccess';
import Signup from './modules/Login/Signup';
import SignupReason from './modules/Login/SignupReason';
import MfaVerify from './modules/Login/MfaVerify';
import EmailVerification from './modules/EmailVerification';
import SignerVerification from './modules/SignerVerification';
import SignerVerificationWithCode from './modules/SignerVerification/SignerVerificationWithCode';
import SignerConfirmData from "./modules/SignerConfirmData"
import Plans from './modules/Plans';
import Logout from './modules/Login/Logout';
import Account from './modules/Account';
import ChangePassword from './modules/Account/ChangePassword';
import UserView from './modules/Users/UserView';
import Profile from './modules/Profile';
import ProfileDelete from './modules/ProfileDelete';
import DocumentRelease from "./modules/DocumentRelease"
import SignedDocumentExpired from "./modules/SignedDocumentExpired"

import BillingHistory from './modules/Billing/History';
import FiscalData from './modules/Billing/FiscalData';
import ExtraDocs from './modules/Billing/ExtraDocs';
import ExtraCredits from './modules/Billing/ExtraCredits';
import PaymentUpdate from './modules/Billing/PaymentUpdate';
import ChangePlan from './modules/Billing/ChangePlan';
import CouponRedeem from './modules/Billing/CouponRedeem';
import AccountIdentification from './modules/Billing/AccountIdentification';
import WorkspaceCreate from './modules/Workspace/WorkspaceCreate';

import Billing from './modules/Billing';
import SignaturePage from "./modules/Document/Sign/SignaturePage"
import CertificateSignPage from "./modules/Document/Sign/CertificateSignPage"

const Dashboard = lazy(() => import('./modules/Dashboard'));

const Templates = lazy(() => import('./modules/Templates/Templates'));
const TemplatePrepare = lazy(() => import('./modules/Template/TemplatePrepare'));
const TemplateSigners = lazy(() => import('./modules/Template/TemplateSigners'));
const TemplateUse = lazy(() => import('./modules/Template/TemplateUse'));
const TemplateView = lazy(() => import('./modules/Template/TemplateView'));
const TemplateReview = lazy(() => import('./modules/Template/TemplateReview'));
const TemplateReviewDocument = lazy(() => import('./modules/Template/TemplateReviewDocument'));
const TemplateSent = lazy(() => import('./modules/Template/TemplateSent'));

const Documents = lazy(() => import('./modules/Documents'));
const DocumentPrepare = lazy(() => import('./modules/Document/DocumentPrepare'));
const DocumentSigners = lazy(() => import('./modules/Document/DocumentSigners'));
const DocumentView = lazy(() => import('./modules/Document/DocumentView'));
const DocumentReview = lazy(() => import('./modules/Document/DocumentReview'));
const DocumentSent = lazy(() => import('./modules/Document/DocumentSent'));
const DocumentSign = lazy(() => import('./modules/Document/DocumentSign'));
const DocumentClosed = lazy(() => import('./modules/Document/DocumentClosed'));
const DocumentSuccess = lazy(() => import('./modules/Document/DocumentSuccess'));
const DocumentsSign = lazy(() => import('./modules/Documents/DocumentsSign'));

const FieldView = lazy(() => import('./modules/Fields/FieldView'));

const Signers = lazy(() => import('./modules/Signers/Signers'));
const SignerView = lazy(() => import('./modules/Signers/SignerView'));

const Integrations = lazy(() => import('./modules/Integrations/Integrations'));

const Contracts = lazy(() => import('./modules/Contracts'));

const Workspace = lazy(() => import('./modules/Workspace'));

const App = () => {
  return (
    <HistoryRouter
      history={browserHistory}
    >
      <Routes>
        <Route path="/" element={<PublicRoute />}>
          <Route path="" element={<RouteItem title="Efetuar Login"><Login /></RouteItem>} />
          <Route path="login-callback" element={<RouteItem title="Efetuar Login"><Login /></RouteItem>} />
          <Route path="mfa" element={<RouteItem title="Verificação em duas etapas"><MfaVerify /></RouteItem>} />
          <Route path="forgot-password/success" element={<RouteItem title="Esqueceu senha"><ForgotPasswordSuccess /></RouteItem>} />
          <Route path="forgot-password" element={<RouteItem title="Esqueceu senha"><ForgotPassword /></RouteItem>} />
          <Route path="password-reset/success" element={<RouteItem title="Resetar senha"><PasswordResetSuccess /></RouteItem>} />
          <Route path="password-reset" element={<RouteItem title="Resetar senha"><PasswordReset /></RouteItem>} />
          <Route path="password-create/success" element={<RouteItem title="Criar senha"><PasswordResetSuccess isNew /></RouteItem>} />
          <Route path="password-create" element={<RouteItem title="Criar senha"><PasswordReset isNew /></RouteItem>} />
          <Route path="signup" element={<RouteItem title="Criar conta"><Signup /></RouteItem>} />
          <Route path="signup/reason" element={<RouteItem title="Criar conta"><SignupReason /></RouteItem>} />
          <Route path="plans" element={<RouteItem title="Planos"><Plans /></RouteItem>} />
          <Route path="email-verification" element={<RouteItem title="Verificar e-mail"><EmailVerification /></RouteItem>} />
          <Route path="verify/:signerAccessCode/code/:code" element={<RouteItem title="Verificando"><SignerVerificationWithCode /></RouteItem>} />
          <Route path="verify/:signerAccessCode" element={<RouteItem title="Verificar e-mail"><SignerVerification /></RouteItem>} />
          <Route path="release/:document_id" element={<RouteItem title="Liberação do documento"><DocumentRelease /></RouteItem>}/>
          <Route path="signed-document/expired" element={<RouteItem title="Link expirado"><SignedDocumentExpired /></RouteItem>} />
          <Route path="invitation/:invitation_id" element={<RouteItem title="Convite"><Signup /></RouteItem>} />
          <Route path="dashboard/documents/:id/sent" element={<RouteItem title="Enviado : Documento"><DocumentSent /></RouteItem>} />
          <Route path="dashboard/templates/:id/sent" element={<RouteItem title="Criado : Template"><TemplateSent /></RouteItem>} />
          <Route path="dashboard/account/change-password" element={<RouteItem title="Alterar senha"><ChangePassword /></RouteItem>} />
          <Route path="dashboard/profile/delete" element={<RouteItem title="Excluir de conta"><ProfileDelete /></RouteItem>} />
          <Route path="dashboard/profile/delete-scheduled" element={<RouteItem title="Agendado : Excluir de conta"><ProfileDelete scheduled={true} /></RouteItem>} />
          <Route path="logout" element={<RouteItem title="Sair"><Logout /></RouteItem>} />
          <Route path="workspace/new" element={<RouteItem title="Criar workspace"><WorkspaceCreate /></RouteItem>} />
        </Route>
        <Route path="/dashboard" element={<DashboardRoute />}>
          <Route path="" element={<RouteItem title="Dashboard"><Dashboard /></RouteItem>} />
          <Route path="documents" element={<RouteItem title="Documentos"><Documents /></RouteItem>} />
          <Route path="documents/:id/view" element={<RouteItem title="Visualizar : Documento"><DocumentView /></RouteItem>} />
          <Route path="templates" element={<RouteItem title="Templates"><Templates /></RouteItem>} />
          <Route path="templates/:id/view" element={<RouteItem title="Visualizar : Template"><TemplateView /></RouteItem>} />
          <Route path="signers" element={<RouteItem title="Seus contatos"><Signers /></RouteItem>} />
          <Route path="signers/:id/view" element={<RouteItem title="Visualizar contato"><SignerView isRead /></RouteItem>} />
          <Route path="signers/:id/edit" element={<RouteItem title="Editar contato"><SignerView /></RouteItem>} />
          <Route path="account/:tab" element={<RouteItem title="Conta"><Account /></RouteItem>} />
          <Route path="account" element={<RouteItem title="Minha Conta"><Account /></RouteItem>} />
          <Route path="contracts" element={<RouteItem title="Gestão de Contratos"><Contracts /></RouteItem>} />
          <Route path="integrations" element={<RouteItem title="Integrações"><Integrations /></RouteItem>} />
          <Route path="integrations/:tab" element={<RouteItem title="Integrações"><Integrations /></RouteItem>} />
          <Route path="profile" element={<RouteItem title="Editar minha conta"><Profile /></RouteItem>} />
          <Route path="workspace/:tab" element={<RouteItem title="Personalizar workspace"><Workspace /></RouteItem>} />
          <Route path="workspace" element={<RouteItem title="Personalizar workspace"><Workspace /></RouteItem>} />
          <Route path="fields/new" element={<RouteItem title="Novo campo"><FieldView /></RouteItem>} />
          <Route path="fields/:id/view" element={<RouteItem title="Campo"><FieldView isRead /></RouteItem>} />
          <Route path="fields/:id/edit" element={<RouteItem title="Editar campo"><FieldView /></RouteItem>} />
          <Route path="users/:id/view" element={<RouteItem title="Usuário"><UserView isRead /></RouteItem>} />
          <Route path="users/:id/edit" element={<RouteItem title="Editar usuário"><UserView /></RouteItem>} />
          {/* temp routes*/}
          <Route path="workspace/billing/history" element={<RouteItem title="Histórico de Cobrança"><BillingHistory /></RouteItem>} />
        </Route>
        <Route path="/dashboard" element={<DashboardRoute hideSidebar={true} />}>
          {/* ^^ essa route faz essas paginas (V) funcionarem*/}
          <Route path="workspace/billing/fiscaldata" element={<RouteItem title="Dados Fiscais"><FiscalData /></RouteItem>} />
          <Route path="workspace/billing/extracredits" element={<RouteItem title="Comprando créditos"><ExtraCredits /></RouteItem>} />
          <Route path="workspace/billing/extradocs" element={<RouteItem title="Comprando documentos"><ExtraDocs /></RouteItem>} />
          <Route path="workspace/billing/paymentupdate" element={<RouteItem title="Informações de Pagamento"><PaymentUpdate /></RouteItem>} />
          <Route path="workspace/billing/changeplan" element={<RouteItem title="Alteração de Plano"><ChangePlan /></RouteItem>} />
          <Route path="workspace/billing/coupon-redeem" element={<RouteItem title="Resgatar cupom"><CouponRedeem /></RouteItem>} />
          <Route path="workspace/billing/account-identification" element={<RouteItem title="Identificação da conta"><AccountIdentification /></RouteItem>} />
            {/* V pra testar os alerts e modals */}  
          <Route path="workspace/billing/menu" element={<RouteItem title="✨Placeholder 5✨"><Billing /></RouteItem>} />
          {/* ----------------------------------------------- */}
        </Route>
        <Route path="/dashboard/documents/:id" element={<PrepareRoute />}>
          <Route path="signers" element={<RouteItem title="Signatários : Documento"><DocumentSigners /></RouteItem>} />
          <Route path="prepare" element={<RouteItem title="Preparação : Documento"><DocumentPrepare /></RouteItem>} />
          <Route path="review" element={<RouteItem title="Revisar e enviar : Documento"><DocumentReview /></RouteItem>} />
        </Route>
        <Route path="/dashboard/documents/:id/virtual" element={<PrepareRoute virtual />}>
          <Route path="signers" element={<RouteItem title="Signatários : Documento"><DocumentSigners virtual /></RouteItem>} />
          <Route path="prepare" element={<RouteItem title="Preparação : Documento"><DocumentPrepare virtual /></RouteItem>} />
          <Route path="review" element={<RouteItem title="Revisar e enviar : Documento"><DocumentReview virtual /></RouteItem>} />
        </Route>
        <Route path="/dashboard/templates/:id" element={<PrepareRouteTemplate />}>
          <Route path="signers" element={<RouteItem title="Signatários : Template"><TemplateSigners /></RouteItem>} />
          <Route path="prepare" element={<RouteItem title="Preparação : Template"><TemplatePrepare /></RouteItem>} />
          <Route path="review" element={<RouteItem title="Revisão e Conclusão : Template"><TemplateReview /></RouteItem>} />
          {/* <Route path="use" element={<RouteItem title="Utilizar : Template"><TemplateUse /></RouteItem>} /> */}
        </Route>
        <Route path="/dashboard/templates/:id" element={<PrepareRouteTemplateUse />}>
          <Route path="use" element={<RouteItem title="Utilizar : Template"><TemplateUse /></RouteItem>} />
          <Route path="review/document" element={<RouteItem title="Revisar e Conclusão : Template"><TemplateReviewDocument /></RouteItem>} />
        </Route>
        <Route path="/dashboard/templates/:id/virtual" element={<PrepareRouteTemplate virtual />}>
          <Route path="signers" element={<RouteItem title="Signatários : Template"><TemplateSigners virtual /></RouteItem>} />
          <Route path="prepare" element={<RouteItem title="Preparação : Template"><TemplatePrepare virtual /></RouteItem>} />
          <Route path="review" element={<RouteItem title="Revisão e Conclusão : Template"><TemplateReview virtual /></RouteItem>} />
        </Route>
        <Route path="/sign/:signerAccessCode" element={<SignRoute />}>
          <Route path="sign-document" element={<RouteItem title="Assinar Documento"><SignaturePage /></RouteItem>} />
          <Route path="sign-certificate" element={<RouteItem title="Assinatura Digital : Documento"><CertificateSignPage /></RouteItem>} />
          <Route path="confirm-data/:document_id" element={<RouteItem title="Confirmar Dados : Assinatura"><SignerConfirmData /></RouteItem>} />
          <Route index element={<RouteItem title="Assinatura : Documento"><DocumentSign /></RouteItem>} />
          <Route path="success" element={<RouteItem title="Assinatura : Documento"><DocumentSuccess /></RouteItem>} />
          <Route path="closed" element={<RouteItem title="Assinatura : Documento"><DocumentClosed /></RouteItem>} />
          <Route path="documents" element={<RouteItem title="Assinatura : Documentos aguardando assinatura"><DocumentsSign /></RouteItem>} />
        </Route>
      </Routes>
    </HistoryRouter>
  );
};

const HistoryRouter = ({
  basename,
  children,
  history
}) => {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  })

  useLayoutEffect(() => {
    history.listen(setState)
  }, [history])

  return <Router
    basename={basename}
    children={children}
    location={state.location}
    navigationType={state.action}
    navigator={history}
  />
}

const RouteItem = ({ title, children }) => {
  useEffect(() => {
    const completePageTitle = `${title ? `${title} | ` : ''}Assinafy`

    document.title = completePageTitle

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'VirtualPageview',
      // virtualPageURL: window.location.href + window.location.search,
      virtualPageTitle: completePageTitle
    })
  }, [title])

  return <Suspense fallback={
    <PageLoader>
      {title}...
    </PageLoader>
  }>{children}</Suspense>
}

export default App;
